import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import useBasket from '../hooks/basket/useBasket';
import { ErrorHandlerInterface, IMap, Order, OrderData } from '../services/exports/Interfaces';
import OrderManager from '../services/api/OrderManager';
import {
  COUPON_TYPES,
  DeliveryProvider,
  DropoffAction,
  ERROR_CODES,
  ORDER_METHODS,
  PaymentMethod,
} from '../services/exports/Constants';
import { debounce } from 'lodash';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import collect from 'collect.js';
import moment from 'moment';
import DuplicateOrderModal from '../modals/checkout/DuplicateOrderModal';
import { PaymentIntent } from '@stripe/stripe-js';
import useAlert from '../hooks/utility/useAlert';
import {
  DataWrappedResponse,
  ErrorResponse,
  ReservedHttpResponseCodes,
  ValidationErrorResponse,
  ValidationErrorResponseData,
} from '../services/exports/Types';
import useAuth from '../hooks/global/useAuth';
import { useTranslation } from 'react-i18next';
import useSessionStorage, { SessionStorageReservedKey } from '../hooks/global/useSessionStorage';
import useDeepRef from '../hooks/utility/useDeepRef';
import { getUtm } from '../services/exports/Cookies';
import useInitialData from '../hooks/global/useInitialData';
import useOffersStuff from '../features/offers/hooks/useOffersStuff';

interface Props {
  children: ReactElement;
}

interface UpdateDataFunction {
  (field: string, value: any): void;
  (data: Partial<OrderData>): void;
}

interface OrderDraftContextInterface {
  data: Order;
  localData: Omit<OrderData, 'line_items'>;
  ongoingOrder: Order;
  loading: boolean;
  isOrderMethodInfoComplete: boolean;
  setOrderMethodInfoInfoComplete: () => void;
  isExternalDelivery: boolean;
  isCustomerInfoComplete: boolean;
  validationErrors: null | ValidationErrorResponseData;
  getOrderData: (data?: Partial<OrderData | { ignored_error_codes: string[] }>) => OrderData;
  draftOrder: (data?: Partial<OrderData>) => Promise<DataWrappedResponse<Order, 200> | ValidationErrorResponse>;
  setDraft: (data: Order) => void;
  setDraftUuid: (uuid: string) => void;
  createOrder: (
    paymentIntent: null | PaymentIntent,
    errorHandlers: IMap<() => void, string>,
    headers?: IMap<string>,
  ) => Promise<
    | DataWrappedResponse<Order, 201>
    | ErrorResponse<
        { order: Order; payment_intent: PaymentIntent },
        ReservedHttpResponseCodes.PaymentConfirmationRequired
      >
    | ErrorResponse<{ error_code: string }, 400>
    | ErrorResponse<any, ReservedHttpResponseCodes.ErrorResponseModal>
    | ErrorResponse<any, ReservedHttpResponseCodes.ErrorResponseAlertWindow>
    | ValidationErrorResponse
  >;
  checkOrderNotDuplicate: () => Promise<boolean>;
  refreshOngoingOrder: () => Promise<null | Order>;
  getOrderMethodName: (order: Partial<Order>) => string;
  addOngoingOrder: (data: Order) => void;
  removeOngoingOrder: (id: number) => void;
  setValidationErrors: (value: null | ValidationErrorResponseData) => void;
  resetValidationErrors: () => void;
  update: UpdateDataFunction;
  resetOrder: (data?: Partial<OrderData>) => void;
}

export const OrderFlowContext = React.createContext<OrderDraftContextInterface>(null);

export enum ORDER_METHOD_INFO_COMPLETION_STATUS {
  complete = 'complete',
}

export default function OrderFlowProvider(props: Props): ReactElement {
  const { t } = useTranslation(undefined, { keyPrefix: 'Providers:OrderFlowProvider' });

  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order, ongoing_orders } = useSelector((state: StoreInterface) => state.order);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const alert = useAlert();
  const { isGuest } = useAuth();
  const { getItem, setItem } = useSessionStorage();
  const { selectedPaymentMethod, shouldSavePaymentInfo } = usePaymentFlow();
  const { items, formatItemForBackend } = useBasket();
  const itemsRef = useDeepRef(items);
  const { forceRefresh } = useInitialData();

  const hasTips = company?.has_tips && selectedPaymentMethod !== PaymentMethod.Cash;
  const [isOrderMethodInfoComplete, setIsOrderMethodInfoInfoComplete] = useState(
    () => getItem(SessionStorageReservedKey.OrderMethodInfoStatus) === ORDER_METHOD_INFO_COMPLETION_STATUS.complete,
  );
  const setOrderMethodInfoInfoComplete = (value: boolean = true) => {
    setItem(
      SessionStorageReservedKey.OrderMethodInfoStatus,
      value ? ORDER_METHOD_INFO_COMPLETION_STATUS.complete : null,
    );
    setIsOrderMethodInfoInfoComplete(value);
  };

  const [draftUuid, setDraftUuid] = useState<string>(null);
  const [draft, setDraft] = useState<Order>(cached_order as Order);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorResponseData>(null);
  const [loading, setLoading] = useState(false);
  const [duplicateOrderAware, setDuplicateOrderAware] = useState<boolean>(false);
  const [duplicateOrderModalProps, setDuplicateOrderModalProps] = useState({
    open: false,
    order: null,
  });
  const { appliedOffers } = useOffersStuff();

  const utm = getUtm() || {};

  const isExternalDelivery = useMemo(
    () =>
      cached_order.method === ORDER_METHODS.DELIVERY && cached_order.delivery_provider !== DeliveryProvider.Standalone,
    [cached_order.method, cached_order.delivery_provider],
  );

  const isCustomerInfoComplete = useMemo(
    () =>
      Boolean(cached_order.customer_phone_number) &&
      Boolean(cached_order.customer_email) &&
      Boolean(cached_order.customer_first_name) &&
      Boolean(cached_order.customer_last_name),
    [
      cached_order.customer_phone_number,
      cached_order.customer_email,
      cached_order.customer_first_name,
      cached_order.customer_last_name,
    ],
  );

  const ongoingOrder = useMemo(
    () => collect(ongoing_orders).firstWhere('company_id', company?.id),
    [ongoing_orders, company?.id],
  );

  const debouncedDraft = debounce(async (data?: OrderData) => draftOrder(data), 250);

  useEffect(() => {
    company?.id && items?.length && debouncedDraft();

    return () => debouncedDraft.cancel();
  }, [
    company?.id,
    items,
    selectedPaymentMethod,
    cached_order.tip,
    cached_order.method,
    cached_order.coupon_type,
    cached_order.coupon_details,
    cached_order.delivery_lat,
    cached_order.delivery_lng,
    cached_order.delivery_quote?.id,
    appliedOffers,
  ]);

  useEffect(() => {
    if (isReduxOrderValid()) {
      return;
    }

    return resetOrder();
  }, []);

  useEffect(() => {
    addMissingOrderFields();
  }, []);

  useEffect(() => {
    refreshOngoingOrder();
  }, []);

  const getOrderData = useCallback(
    (data?: Partial<OrderData>): OrderData => ({
      ...cached_order,
      method: cached_order.method,
      company_id: company?.id,
      payment_method_id: draft.total > 0 ? selectedPaymentMethod : PaymentMethod.Cash,
      should_save_payment_info: shouldSavePaymentInfo,
      delivery_quote_id: isExternalDelivery ? cached_order.delivery_quote?.id : null,
      delivery_dropoff_action: isExternalDelivery
        ? (cached_order.delivery_dropoff_action ?? DropoffAction.RingDoorBell)
        : null,
      promo: cached_order.coupon_details?.code,
      use_cashback: cached_order.coupon_type === COUPON_TYPES.CASHBACK,
      tip: !hasTips ? 0 : (cached_order.tip ?? 0),
      line_items: itemsRef.current.map((item) => formatItemForBackend(item)),
      applied_offers: appliedOffers?.map((appliedOffer) => ({ id: appliedOffer.id })),
      utm,
      ...data,
    }),
    [
      appliedOffers,
      cached_order,
      company?.id,
      draft?.total,
      hasTips,
      isExternalDelivery,
      selectedPaymentMethod,
      shouldSavePaymentInfo,
      itemsRef.current,
      utm,
    ],
  );

  const handleOrderDraftResult = (order: Order) => {
    setDraft(order);

    if (!appliedOffers?.length && !order.applied_offers?.length) {
      return;
    }

    const hasOffersMismatch =
      appliedOffers?.length !== order.applied_offers?.length ||
      appliedOffers?.some(
        (appliedOffer) => !order.applied_offers?.some((orderOffer) => orderOffer.id === appliedOffer.id),
      );

    hasOffersMismatch && forceRefresh();
  };

  async function draftOrder(data?: OrderData) {
    setLoading(true);
    const request = await OrderManager.draftOrder({
      ...getOrderData(data),
      payment_intent_id: 'draft',
    });
    setLoading(false);

    const { response, success } = request;

    if (success) {
      handleOrderDraftResult(response.data.data);
    }

    return request;
  }

  async function createOrder(
    paymentIntent: null | PaymentIntent,
    errorHandlers: IMap<() => void, string> = {},
    headers: IMap<string> = {},
  ) {
    const params: [OrderData, ErrorHandlerInterface, IMap<string>] = [
      getOrderData({
        payment_intent_id: paymentIntent?.id,
      }),
      {
        [ERROR_CODES.CARD_EXCEPTION]: () => null,
        [ERROR_CODES.ORDER_FULFILLMENT_FAILED]: () => null,
        [ERROR_CODES.PICKUP_CLOSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_CLOSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.DELIVERY_QUOTE_ALREADY_USED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ORDERS_PAUSED]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ROOM_SERVICE_LOCATION_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        [ERROR_CODES.ROOM_SERVICE_UNAVAILABLE]: () => setOrderMethodInfoInfoComplete(false),
        ...errorHandlers,
      },
      headers,
    ];

    const response = draftUuid
      ? await OrderManager.updateOrder(draftUuid, ...params)
      : await OrderManager.createOrder(...params);

    if (!response.success) {
      orderErrorHandler(response);

      return response;
    }

    return response;
  }

  function orderErrorHandler(error: any): void {
    if (error?.response?.status === ReservedHttpResponseCodes.ErrorResponseAlertWindow) {
      alert.error({
        title: error?.response?.data?.title,
        description: error?.response?.data?.description,
      });
    }
  }

  async function checkOrderNotDuplicate(): Promise<boolean> {
    if (duplicateOrderAware || isGuest) {
      return true;
    }

    const { success, response } = await OrderManager.getOrderHistory({ limit: 1 }, undefined, company?.id);

    if (!success) {
      return true;
    }

    const order = collect(response.data.data).first();

    if (order && moment().diff(moment(order.paid_at), 'minutes') < 15) {
      toggleDuplicateOrderModal(order);

      return false;
    }

    return true;
  }

  async function refreshOngoingOrder(): Promise<null | Order> {
    if (!ongoingOrder) {
      return;
    }

    const { success, response } = await OrderManager.getOrder(ongoingOrder.uuid);

    if (!success) {
      return;
    }

    if (moment(response.data.data.fulfillment.estimated_pickup_time).isBefore(moment().subtract(15, 'minutes'))) {
      ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(ongoingOrder.id));

      return null;
    }

    ReduxActions.dispatch(actionCreators.order.addOngoingOrder(response.data.data));

    return response.data.data;
  }

  function resetOrder(data: Partial<OrderData> = {}) {
    const payload = {
      ...data,
      ...(profile
        ? {
            customer_phone_number: profile.phone_number,
            customer_email: profile.email,
            customer_first_name: profile.first_name,
            customer_last_name: profile.last_name,
            marketing_consent: profile.is_marketing_subscriber,
          }
        : {}),
    };

    setDraftUuid(null);
    ReduxActions.dispatch(actionCreators.order.resetOrder(payload));
  }

  function addMissingOrderFields() {
    if (!profile) {
      return;
    }

    const replace = collect({
      customer_phone_number: profile.phone_number,
      customer_email: profile.email,
      customer_first_name: profile.first_name,
      customer_last_name: profile.last_name,
    }).filter((value, key) => !cached_order[key]).items;

    ReduxActions.dispatch(actionCreators.order.updateOrder(replace));
  }

  function isReduxOrderValid(): boolean {
    return !!cached_order?.method;
  }

  function toggleDuplicateOrderModal(order: Order = null): void {
    setDuplicateOrderModalProps({
      open: !!order,
      order,
    });
  }

  function getOrderMethodName(order: Partial<Order>): string {
    if (order.method === ORDER_METHODS.ROOM_SERVICE) {
      t(`order_methods.${order.method}.${order.room_service_location.type}`);
    }

    return t(`order_methods.${order.method}`);
  }

  const update: UpdateDataFunction = (field: string | Partial<OrderData>, value?: any) => {
    if (typeof field === 'object') {
      return ReduxActions.dispatch(actionCreators.order.updateOrder(field));
    }

    const parts = field.split('.');
    const key = parts[0];

    if (parts.length === 1) {
      return ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          [key]: value,
        }),
      );
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        [key]: {
          ...cached_order[key],
          [parts[1]]: value,
        },
      }),
    );
  };

  const context = useMemo(
    () => ({
      data: draft,
      localData: cached_order,
      ongoingOrder,
      loading,
      isOrderMethodInfoComplete,
      setOrderMethodInfoInfoComplete,
      isExternalDelivery,
      isCustomerInfoComplete,
      validationErrors,
      getOrderData,
      draftOrder,
      setDraft,
      setDraftUuid,
      createOrder,
      checkOrderNotDuplicate,
      refreshOngoingOrder,
      getOrderMethodName,
      addOngoingOrder: (data: Order) => ReduxActions.dispatch(actionCreators.order.addOngoingOrder(data)),
      removeOngoingOrder: (id: number) => ReduxActions.dispatch(actionCreators.order.removeOngoingOrder(id)),
      setValidationErrors,
      resetValidationErrors: () => setValidationErrors(null),
      update,
      resetOrder,
    }),
    [
      draft,
      loading,
      cached_order,
      ongoingOrder,
      isOrderMethodInfoComplete,
      isCustomerInfoComplete,
      validationErrors,
      draftOrder,
      getOrderData,
    ],
  );

  const renderDuplicateOrderModal = useMemo(() => {
    return (
      <DuplicateOrderModal
        open={duplicateOrderModalProps.open}
        order={duplicateOrderModalProps.order}
        onConfirm={() => setDuplicateOrderAware(true)}
        toggleModal={toggleDuplicateOrderModal}
      />
    );
  }, [duplicateOrderModalProps]);

  return (
    <OrderFlowContext.Provider value={context}>
      {children}
      {renderDuplicateOrderModal}
    </OrderFlowContext.Provider>
  );
}
